import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';
import { useApi } from '../utils/useApi';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import PasswordChangeSuccessModal from '../components/PasswordChangeSuccessModal';

// Gym-themed avatars
const GYM_AVATARS = [
  { id: 'dumbbell-1', name: 'Dumbbell', emoji: '🏋️', color: 'from-blue-500 to-blue-600' },
  { id: 'dumbbell-2', name: 'Dumbbell', emoji: '💪', color: 'from-purple-500 to-purple-600' },
  { id: 'barbell-1', name: 'Barbell', emoji: '🏋️‍♂️', color: 'from-red-500 to-red-600' },
  { id: 'barbell-2', name: 'Barbell', emoji: '🏋️‍♀️', color: 'from-green-500 to-green-600' },
  { id: 'trophy-1', name: 'Trophy', emoji: '🏆', color: 'from-yellow-500 to-yellow-600' },
  { id: 'trophy-2', name: 'Trophy', emoji: '🥇', color: 'from-orange-500 to-orange-600' },
  { id: 'fitness-1', name: 'Fitness', emoji: '💪', color: 'from-indigo-500 to-indigo-600' },
  { id: 'fitness-2', name: 'Fitness', emoji: '🔥', color: 'from-pink-500 to-pink-600' },
  { id: 'gym-1', name: 'Gym', emoji: '🏋️', color: 'from-teal-500 to-teal-600' },
  { id: 'gym-2', name: 'Gym', emoji: '⚡', color: 'from-cyan-500 to-cyan-600' },
  { id: 'strength-1', name: 'Strength', emoji: '💪', color: 'from-amber-500 to-amber-600' },
  { id: 'strength-2', name: 'Strength', emoji: '🎯', color: 'from-violet-500 to-violet-600' },
];

interface BranchOption {
  id: string;
  name: string;
  displayName: string | null;
}

interface UserProfile {
  id: string;
  name: string | null;
  displayName: string | null;
  email: string | null;
  phoneNo: string | null;
  avatar: string | null;
  lastLoginAt: string | null;
  defaultBranchId: string | null;
  roles: Array<{
    type: string;
    gymId: string;
    gymName: string;
    role: string;
    branchId?: string;
    branchName?: string;
  }>;
}

export default function UserProfilePage() {
  const { user: authUser, signOut } = useAuth();
  const { refetch } = useUserContext();
  const { setSelectedBranchId } = useBranch();
  const { fetchWithAuth } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    avatar: null as string | null,
    defaultBranchId: '' as string
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchProfile();
    fetchBranches();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/auth/me');
      if (response.success) {
        const userData = response.data;
        setProfile(userData);
        setFormData({
          name: userData.name || '',
          displayName: userData.displayName || userData.name || '',
          avatar: userData.avatar || null,
          defaultBranchId: userData.defaultBranchId || ''
        });
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetchWithAuth('/api/branches');
      const list: BranchOption[] = (response.data || response.branches || []).filter(
        (b: any) => (b.status ?? 'active') !== 'inactive'
      );
      setBranches(list);
    } catch {
      /* non-fatal: default branch is optional */
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await fetchWithAuth('/api/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        toast.success('Profile updated successfully');
        // Apply the chosen default branch to the active scope immediately, so the
        // change takes effect now instead of only on the next fresh login. A blank
        // default clears the scope back to "All branches".
        setSelectedBranchId(formData.defaultBranchId || null);
        await fetchProfile();
        // Refresh user context
        await refetch();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const errors: Record<string, string> = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setChangingPassword(true);
      const response = await fetchWithAuth('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.success) {
        // Clear password fields
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordErrors({});

        // Show success modal
        setShowPasswordSuccessModal(true);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordChangeConfirm = async () => {
    try {
      // Clear localStorage first
      localStorage.clear();

      // Then sign out
      await signOut();

      // Use window.location.replace to prevent back button issues
      window.location.replace('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear localStorage anyway
      localStorage.clear();
      // Force redirect even if logout fails
      window.location.replace('/login');
    }
  };

  const getAvatarDisplay = (avatarId: string | null) => {
    if (!avatarId) return null;
    const avatar = GYM_AVATARS.find(a => a.id === avatarId);
    return avatar || null;
  };

  const getInitials = () => {
    const name = formData.displayName || formData.name || authUser?.email?.split('@')[0] || 'U';
    return name.charAt(0).toUpperCase();
  };

  const selectedAvatar = getAvatarDisplay(formData.avatar);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load profile</p>
      </div>
    );
  }

  return (
    <>
      {/* Password Change Success Modal */}
      <PasswordChangeSuccessModal
        isOpen={showPasswordSuccessModal}
        onConfirm={handlePasswordChangeConfirm}
      />

      <div className="max-w-4xl mx-auto space-y-6 pb-20 sm:pb-0 safe-area-inset-bottom">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar Display */}
            <div className="flex-shrink-0">
              {selectedAvatar ? (
                <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${selectedAvatar.color} flex items-center justify-center text-4xl shadow-lg`}>
                  {selectedAvatar.emoji}
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {getInitials()}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {formData.displayName || formData.name || 'User'}
              </h1>
              <p className="text-gray-600 mb-1">{profile.email}</p>
              {profile.phoneNo && (
                <p className="text-gray-600 mb-4">{profile.phoneNo}</p>
              )}
              {profile.lastLoginAt && (
                <p className="text-sm text-gray-500">
                  Last login: {new Date(profile.lastLoginAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Information</h2>

          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your full name"
              />
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your display name"
              />
            </div>

            {/* Default Branch */}
            {branches.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Branch
                </label>
                <select
                  value={formData.defaultBranchId}
                  onChange={(e) => setFormData({ ...formData, defaultBranchId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All branches (no default)</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  When set, members, plans and purchases load pre-filtered to this branch. You can
                  still switch scope any time from the branch selector in the top bar.
                </p>
              </div>
            )}

            {/* Avatar Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Choose Avatar
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {GYM_AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setFormData({ ...formData, avatar: avatar.id })}
                    className={`aspect-square rounded-xl flex items-center justify-center text-2xl transition-all ${formData.avatar === avatar.id
                      ? `bg-gradient-to-br ${avatar.color} scale-110 shadow-lg ring-2 ring-blue-500`
                      : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                  >
                    {avatar.emoji}
                  </button>
                ))}
                <button
                  onClick={() => setFormData({ ...formData, avatar: null })}
                  className={`aspect-square rounded-xl flex items-center justify-center text-xl font-bold transition-all ${formData.avatar === null
                    ? 'bg-gradient-to-br from-gray-500 to-gray-600 text-white scale-110 shadow-lg ring-2 ring-blue-500'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  title="Use Initials"
                >
                  {getInitials()}
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Roles & Permissions */}
        {profile.roles && profile.roles.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Roles & Permissions</h2>
            <div className="space-y-4">
              {profile.roles.map((role, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900 capitalize">{role.role}</span>
                    <span className="text-sm text-gray-600 capitalize">{role.type}</span>
                  </div>
                  <p className="text-sm text-gray-600">{role.gymName}</p>
                  {role.branchName && (
                    <p className="text-sm text-gray-500">Branch: {role.branchName}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Change Password */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Change Password</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter current password"
              />
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter new password (min 8 characters)"
              />
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm new password"
              />
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
              )}
            </div>

            <div className="pt-2">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="w-full sm:w-auto px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
